import { MutationUpdaterFn } from "@apollo/client";
import { FormikHelpers } from "formik/dist/types";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useState } from "react";
import * as yup from "yup";

import { FormikDialog } from "@/features/common/components/dialog/FormikDialog";
import { BaseDialogProps } from "@/features/common/components/dialog/type";
import { useUIContext } from "@/features/common/hooks/UIContext/useUIContext";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { useHandleGraphqlError } from "@/features/common/hooks/useHandleGraphqlError";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  ReportPostInput,
  ReportPostMutation,
  useReportPostMutation,
} from "@/generated/generated-hooks";

const TextAreaAutoResizeInputField = dynamic(
  () =>
    import(
      "@/features/common/components/formik/TextAreaAutoResizeInputField"
    ).then((mod) => mod.TextAreaAutoResizeInputField),
  {
    ssr: false,
  },
);

type ReportPostInfo = {
  _id: string;
  title: string;
  writerId: string;
  slug: string;
};

export type ReportPostDialogProps = Pick<
  BaseDialogProps,
  "open" | "onClose"
> & {
  post?: ReportPostInfo;
};

export const ReportPostDialog: React.FC<ReportPostDialogProps> = (props) => {
  const { onClose, post } = props;
  const { setReportPostDialog } = useUIContext();
  const [open, setOpen] = useState(false);
  const { user } = useUserContext();
  const { t } = useTranslation("common");
  const [reportPost, { data, error, loading }] = useReportPostMutation({
    update: reportPostUpdateFn(post?._id),
  });
  const handleGraphqlError = useHandleGraphqlError();

  const { openSnackbar } = useSnackbar();
  useEffect(() => {
    setOpen(props.open ?? false);
  }, [props.open]);

  const validationSchema = useMemo(() => {
    const exceedWordLimitMessage = (maxLength: number) =>
      t("common:formValidation.required", { maxLength });
    return yup.object().shape({
      content: yup
        .string()
        .test("len", exceedWordLimitMessage(40), (val = "") => !!val.trim()),
    });
  }, [t]);

  const onSubmit = async (values, action: FormikHelpers<any>) => {
    try {
      await reportPost({
        variables: { input: { ...values, content: values.content.trim() } },
      });
      openSnackbar({
        message: t("common:post.report.success"),
        severity: "success",
      });
      setReportPostDialog((s) => ({ ...s, open: false }));
    } catch (error) {
      handleGraphqlError({
        e: error,
        t,
        defaultErrorTranslationKey: "common:post.report.fail",
        sentryCaptureContext: {
          extra: {
            postId: post?._id,
          },
        },
      });
    }
  };

  const initialValues = useMemo(() => {
    if (!post || !user?._id) return null;
    return getReportPostInitialValues(post, user._id);
  }, [post, user?._id]);

  return (
    <FormikDialog<ReportPostInput>
      initialValues={initialValues}
      title={t("common:post.report.title", { title: post?.title })}
      message={
        <TextAreaAutoResizeInputField
          className="border-black mb-4"
          name="content"
          rowsMin={5}
          placeholder={t("common:post.report.placeholder")}
        />
      }
      unsavedMessage={t("common:post.report.cancelAlert")}
      confirmButtonLabel={t("common:post.report.confirmButtonLabel")}
      onSubmit={onSubmit}
      onClose={onClose}
      open={open}
      enableReinitialize={true}
      validationSchema={validationSchema}
    />
  );
};

const getReportPostInitialValues = (
  post: ReportPostInfo,
  reporterId: string,
): ReportPostInput => {
  const { _id, writerId } = post;
  return { postId: _id, reporterId, writerId, content: "" };
};

const reportPostUpdateFn: (
  postId: string,
) => MutationUpdaterFn<ReportPostMutation> =
  (postId) =>
  (cache, { data: { reportPost } }) => {
    if (!reportPost) return;
    cache.modify({
      id: cache.identify({ __typename: "Post", _id: postId }),
      fields: {
        hasCurrentUserReport() {
          return true;
        },
      },
    });
  };
